import React from 'react';
import { toast } from 'sonner';
import {
  FileText, BookOpen, Brain, Shield, Zap, ArrowRight
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const CAPABILITY_LABELS = {
  description_review: { label: 'Description Review', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  kb_update: { label: 'Knowledge Update', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50' },
  loi_prompt: { label: 'LOI Prompt', icon: Brain, color: 'text-amber-600', bg: 'bg-amber-50' },
  compliance: { label: 'Compliance', icon: Shield, color: 'text-green-600', bg: 'bg-green-50' },
};

const AdminTradosAgentOverview = () => {
  const createTask = async (capability, refreshStats) => {
    try {
      await api.post('/admin/trados-agent/tasks', { capability, priority: 'normal' });
      toast.success('Task created');
      if (refreshStats) refreshStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create task'); }
  };

  return (
    <AgentLayout>
      {({ stats, refreshStats }) => stats ? (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.tasks.total}</p>
              <div className="flex gap-2 mt-2 text-xs">
                {stats.tasks.queued > 0 && <span className="text-gray-500">{stats.tasks.queued} queued</span>}
                {stats.tasks.running > 0 && <span className="text-blue-600">{stats.tasks.running} running</span>}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Suggestions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.suggestions.total}</p>
              <div className="flex gap-2 mt-2 text-xs">
                {stats.suggestions.pending_review > 0 && <span className="text-amber-600">{stats.suggestions.pending_review} to review</span>}
                {stats.suggestions.applied > 0 && <span className="text-green-600">{stats.suggestions.applied} applied</span>}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Chat Sessions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.chat_conversations}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Tokens This Month</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{(stats.tokens_this_month || 0).toLocaleString()}</p>
            </div>
          </div>

          {/* Capabilities */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(CAPABILITY_LABELS).map(([key, cap]) => {
                const Icon = cap.icon;
                return (
                  <div key={key} className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50">
                    <div className={`w-10 h-10 rounded-lg ${cap.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${cap.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{cap.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {key === 'description_review' && 'Review and improve company descriptions'}
                        {key === 'kb_update' && 'Analyze chats and update knowledge bases'}
                        {key === 'loi_prompt' && 'Improve LOI analysis prompts from results'}
                        {key === 'compliance' && 'Research users and companies for verification'}
                      </p>
                    </div>
                    <button
                      onClick={() => createTask(key, refreshStats)}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 flex-shrink-0"
                    >
                      Run <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </AgentLayout>
  );
};

export default AdminTradosAgentOverview;
